/**
 * What-If Analysis canvas (design doc executive-view-design/
 * EXEC_WHATIF_v1_design.md §3–§4).
 *
 * Owns two independent pieces of state:
 *  - `canvas`: the state machine — exactly one of HOME, CONFIRM,
 *    PREPARING, PROPOSAL, RECALCULATING, RESULT (§4), plus a
 *    'proposal_readonly' variant (§11.2 "View approved changes") that is
 *    not one of the doc's six canvas states but a read-only overlay of
 *    PROPOSAL reachable only from RESULT and returning only to RESULT —
 *    it never appears in HOME/CONFIRM's transition table because nothing
 *    outside RESULT can reach it.
 *  - form state (`outputId`/`inputId`/`changeText`) + `activeRun`
 *    (plan_id of the run currently in flight or reopened) — these
 *    survive canvas transitions. Going HOME never clears the form
 *    (design doc CONFIRM row "← Examples & recents → HOME (form
 *    retained)"; same retention on PROPOSAL's Discard exit and on a
 *    failed propose/discard/approve call, §6).
 *
 * Layout (§3): a flex column filling the pane. The canvas region — a
 * full-width scroll box wrapping an EXEC_CONTENT_CONTAINER_SX block
 * (two boxes on purpose; see the render comment) — sits above the
 * Control Bar (border-top). Nothing ever renders below the bar (D10) —
 * the bar is the last flex child, full stop, in every canvas state.
 *
 * Run wiring (§4 CONFIRM row, §6 failure treatment): Run transitions to
 * PREPARING and immediately calls `proposeWhatIf`, guarded by the same
 * monotonic `runSeq` stale-response idiom AnswerView.tsx uses — a slow
 * propose response from an earlier Run can never clobber a newer one
 * (including "re-run while a proposal is displayed," which the design
 * doc treats as backend-superseding the old plan, D4). Success lands on
 * PROPOSAL with the returned payload; failure returns a calm
 * severity="info" failure state (own placeholder within the canvas
 * region, not a separate CanvasState variant — it carries the same
 * "← Examples & recents" affordance already rendered above the canvas
 * for every non-home state) and leaves the form untouched.
 *
 * RECALCULATING poll (Task 14, §4 RECALCULATING row): entering
 * 'recalculating' starts a 2 s poll of `getWhatIfRun`, guarded by
 * `recalcPollRef`/`recalcPlanRef` (see their declarations) so at most
 * one interval ever runs and a brand switch or unmount can never leave
 * an orphaned timer ticking against a stale plan. 'completed' -> RESULT;
 * 'failed'/'cancelled' -> the calm failure copy inline on the same
 * canvas slot (mirrors PREPARING's own error-inline pattern above,
 * rather than a bounce to HOME — the executive's last action stays on
 * screen next to its outcome, same rationale as the PREPARING case);
 * ~6 minutes of elapsed polling with no terminal status -> a "taking
 * longer than expected" copy variant while the SAME interval keeps
 * polling in the background (never destructive — a late completion
 * still lands the executive on RESULT) until the canvas leaves
 * 'recalculating' or the component unmounts.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, alpha, Box, Button, Typography } from '@mui/material';
import { ArrowBack, WarningAmberOutlined } from '@mui/icons-material';

import { EXEC_CONTENT_CONTAINER_SX } from '../layout';
import {
  ExecutiveBrand,
  WhatIfExample,
  WhatIfOptions,
  WhatIfProposal,
  WhatIfRecentRun,
  WhatIfResult,
  WhatIfRunStatus,
} from '../types';
import { YearWindow } from '../execYearWindow';
import {
  approveWhatIf,
  discardWhatIf,
  dismissWhatIfRun,
  getWhatIfRecent,
  getWhatIfRun,
  proposeWhatIf,
} from '../api';

import ControlBar from './ControlBar';
import HomeState, { HomeTab, statusLine } from './HomeState';
import ConfirmState from './ConfirmState';
import PreparingState from './PreparingState';
import ProposalState, { canApproveProposal } from './ProposalState';
import RecalculatingState from './RecalculatingState';
import ResultState from './ResultState';

// §4 RECALCULATING row failure copy (verbatim, §6): "The recalculation
// didn't complete. Your proposed changes were not applied to any
// forecast."
const RECALC_FAILED_COPY =
  "The recalculation didn't complete. Your proposed changes were not applied to any forecast.";
// Timeout copy variant (task brief) — the poll keeps running regardless.
const RECALC_TIMEOUT_COPY =
  'This is taking longer than expected… Your changes are still being applied — this page will update automatically when it finishes.';

const RECALC_POLL_INTERVAL_MS = 2000;
const RECALC_TIMEOUT_MS = 6 * 60 * 1000;
// PREPARING poll (B2): propose is now kickoff-then-poll like approve, so the
// FE polls the run for live stage progress + the finished proposal. Shorter
// interval than recalc — propose is quick (segment selection + patch planning,
// ~15-45s) and the point is a responsive stage list.
const PROPOSE_POLL_INTERVAL_MS = 1500;
const PROPOSE_TIMEOUT_MS = 3 * 60 * 1000;

interface WhatIfViewProps {
  brand: ExecutiveBrand;
  // The single scenario What-If operates against (D1). Read by
  // handleRun (WhatIfProposeRequest.scenario_id/scenario_names) and by
  // the scenarioName lookup ProposalState's headline uses.
  scenarioId: number;
  options: WhatIfOptions;
  // Reports whether the RESULT screen is showing, so ForecastEdgeAITab can hide its overlaid Analyst/Executive
  // toggle there — the band has room for the back link and the disclaimer, not the toggle as well (2026-09-28).
  // Must be a stable function (a state setter): it is an effect dependency.
  onResultShownChange?: (shown: boolean) => void;
}

// The canvas state machine (design doc §4). Each variant's payload is
// exactly what that state needs to render. 'preparing' carries an
// optional `error` — a failed propose stays structurally in the
// PREPARING slot (design doc §6 gives PREPARING failures their own calm
// copy + return-home affordance, not a seventh state) rather than
// bouncing back to CONFIRM, so the executive's last action ("I hit Run")
// and the resulting failure read as the same screen. 'recalculating'
// mirrors that pattern: `error` (terminal failure copy) and `timedOut`
// (non-terminal, poll keeps running) are both inline flags on the same
// slot, never separate states. 'result' carries the full fetched run
// (`result`/`proposal`) so ResultState never needs a second fetch, and
// so "View approved changes" (proposal_readonly) can hand the exact same
// payload back without re-fetching.
type CanvasState =
  | { state: 'home' }
  // `notice` carries the status line of a reopened non-completed run
  // ("Cancelled — nothing was applied.") so CONFIRM can say why the
  // canvas moved (2026-08-23: every recent click navigates; the silent
  // stay-on-HOME seed read as a dead click and could overwrite manual
  // edits with no visual response).
  | { state: 'confirm'; notice?: string }
  // `progress` carries the live PREPARING stage snapshot (B2) so PreparingState
  // can render a stage list; absent until the first poll returns.
  | {
      state: 'preparing';
      progress?: WhatIfRunStatus['progress'];
      error?: string;
    }
  // `scenarioId` on the run-anchored states below is the run's OWN
  // scenario, set on the reopen paths (2026-08-23): a reopened run may
  // belong to a different scenario than the sidebar's current selection,
  // and the headline must name the scenario Approve would actually write
  // to. Absent on the fresh-run flow, where the current selection IS the
  // run's scenario and the lookup falls back to it.
  | {
      state: 'proposal';
      planId: string;
      proposal: WhatIfProposal;
      scenarioId?: number;
    }
  | {
      state: 'recalculating';
      planId: string;
      progress?: WhatIfRunStatus['progress'];
      error?: string;
      timedOut?: boolean;
    }
  | {
      state: 'result';
      planId: string;
      result: WhatIfResult;
      proposal?: WhatIfProposal | null;
      scenarioId?: number;
    }
  // §11.2 "View approved changes" — read-only overlay of PROPOSAL,
  // reachable only from RESULT (see this file's header comment).
  | {
      state: 'proposal_readonly';
      planId: string;
      proposal: WhatIfProposal;
      result: WhatIfResult;
      scenarioId?: number;
    };

interface WhatIfFormState {
  outputId: number | '';
  inputId: number | '';
  changeText: string;
}

const EMPTY_FORM: WhatIfFormState = {
  outputId: '',
  inputId: '',
  changeText: '',
};

const WhatIfView: React.FC<WhatIfViewProps> = ({
  brand,
  scenarioId,
  options,
  onResultShownChange,
}) => {
  const [canvas, setCanvas] = useState<CanvasState>({ state: 'home' });
  const [form, setForm] = useState<WhatIfFormState>(EMPTY_FORM);
  // plan_id of the run this canvas is currently showing or has most
  // recently reopened. Set by handleOpenRun on a reopened completed run,
  // and by a successful propose ("the run this canvas is about," in
  // flight or reopened). Write-only today — the value slot is
  // deliberately not destructured (2026-08-24: CI builds run CRA with
  // CI=true, which promotes the unused-var warning to a build error;
  // this was the sole failure on the branch's first PR). Restore the
  // binding when a reader appears (the history tracker is the likely
  // consumer).
  const [, setActiveRun] = useState<string | null>(null);

  // Discard/approve in-flight + failure state for the PROPOSAL footer
  // actions (§6 calm inline failure — the proposal stays on screen).
  // Not part of CanvasState because it doesn't change *what* is
  // rendered, only in-place button/alert state on top of the same
  // 'proposal' canvas value.
  const [discarding, setDiscarding] = useState(false);
  const [approving, setApproving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [recents, setRecents] = useState<WhatIfRecentRun[]>([]);
  const [recentsLoading, setRecentsLoading] = useState(false);

  // Monotonic stale-response guard for propose (same idiom as
  // AnswerView.tsx's runSeq): incremented on every Run click; a
  // response is only applied if runSeq is still the value captured at
  // fire time. Covers both "user Ran again before the first response
  // landed" and "re-running while a proposal is displayed" (§4 PROPOSAL
  // row: "New Run → supersedes, → PREPARING" — the backend marks the
  // old plan superseded; this ref just stops a late-arriving old
  // response from overwriting the new one's PREPARING/PROPOSAL render).
  const runSeq = useRef(0);

  // RECALCULATING poll lifecycle (Task 14). `recalcPollRef` holds the
  // single live setTimeout id (recursive setTimeout, not setInterval, so
  // a slow response can never cause two in-flight requests to overlap).
  // `recalcPlanRef` is the plan_id the currently-running poll loop is
  // for — every scheduled tick re-checks it against `recalcPlanRef` and
  // no-ops if it's changed (canvas moved on, a different run started
  // polling, or the component is tearing down), which is what makes
  // "stop polling" and "start a new poll" both a single source of truth
  // instead of a race between an old interval's cleanup and a new
  // effect's setup. `recalcStartRef` timestamps when the CURRENT poll
  // loop began, for the ~6-minute timeout copy switch (timeout does NOT
  // stop the loop — see startRecalcPoll).
  const recalcPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recalcPlanRef = useRef<string | null>(null);
  const recalcStartRef = useRef<number>(0);

  const stopRecalcPoll = useCallback(() => {
    if (recalcPollRef.current !== null) {
      clearTimeout(recalcPollRef.current);
      recalcPollRef.current = null;
    }
    recalcPlanRef.current = null;
  }, []);

  // PREPARING poll lifecycle (B2), same guard idiom as the recalc poll:
  // `proposePollRef` holds the live setTimeout id, `proposePlanRef` the plan
  // the loop is for (every tick re-checks it so a stale tick no-ops), and
  // `proposeStartRef` timestamps the loop for the timeout flag.
  const proposePollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const proposePlanRef = useRef<string | null>(null);
  const proposeStartRef = useRef<number>(0);

  const stopProposePoll = useCallback(() => {
    if (proposePollRef.current !== null) {
      clearTimeout(proposePollRef.current);
      proposePollRef.current = null;
    }
    proposePlanRef.current = null;
  }, []);

  // Unmount safety net, mirrors the recalc one.
  useEffect(() => stopProposePoll, [stopProposePoll]);

  // Unmount safety net — belt-and-suspenders alongside every canvas
  // transition below explicitly calling stopRecalcPoll(): guarantees no
  // orphaned timer survives WhatIfView unmounting (e.g. navigating away
  // from the What-If tab entirely) even if some future edit adds a
  // canvas transition that forgets to call it.
  useEffect(() => stopRecalcPoll, [stopRecalcPoll]);

  // Starts (or restarts) the RECALCULATING poll for `planId`. Safe to
  // call re-entrantly — it always stops whatever loop was previously
  // running first, so approving a run while a stale loop from a
  // different plan was somehow still ticking can't produce two loops.
  const startRecalcPoll = useCallback(
    (planId: string) => {
      stopRecalcPoll();
      recalcPlanRef.current = planId;
      recalcStartRef.current = Date.now();

      const tick = () => {
        // The plan this loop is for changed (canvas moved on, a newer
        // poll started, or stopRecalcPoll ran) between scheduling this
        // tick and it firing — no-op rather than acting on stale state.
        if (recalcPlanRef.current !== planId) return;

        getWhatIfRun(planId)
          .then((run) => {
            if (recalcPlanRef.current !== planId) return;

            // A 'completed' status without a usable result payload is
            // treated the same as a failure (§6 tone: never a broken
            // RESULT screen) rather than trusting the cast the earlier
            // reopen path (handleOpenRun) guards the same way.
            if (run.status === 'completed' && run.result) {
              stopRecalcPoll();
              setCanvas({
                state: 'result',
                planId,
                result: run.result,
                proposal: run.proposal ?? null,
                // The run's own scenario — the sidebar selection may
                // have changed while the poll ran (see CanvasState).
                scenarioId: run.scenario_id,
              });
              return;
            }

            if (
              run.status === 'failed' ||
              run.status === 'cancelled' ||
              (run.status === 'completed' && !run.result)
            ) {
              // Terminal, non-success: stop polling and show the calm
              // §6 failure copy inline on the same RECALCULATING slot.
              // The plan is retained in recents by the backend — no
              // action needed here beyond leaving the "← Examples &
              // recents" affordance (already rendered above the canvas)
              // as the path home.
              stopRecalcPoll();
              setCanvas({
                state: 'recalculating',
                planId,
                error: RECALC_FAILED_COPY,
              });
              return;
            }

            // Still non-terminal ('executing' or otherwise): update the
            // progress snapshot in place and reschedule. Compute the
            // timeout flag fresh each tick off recalcStartRef rather
            // than trusting the previous canvas value, so a timeout
            // reached while the canvas briefly rendered something else
            // (shouldn't happen, but defensively) still gets flagged.
            const timedOut =
              Date.now() - recalcStartRef.current > RECALC_TIMEOUT_MS;
            setCanvas({
              state: 'recalculating',
              planId,
              progress: run.progress,
              timedOut,
            });
            recalcPollRef.current = setTimeout(tick, RECALC_POLL_INTERVAL_MS);
          })
          .catch(() => {
            if (recalcPlanRef.current !== planId) return;
            // A transient poll-request failure (network blip) is not
            // itself a recalculation failure — keep polling rather than
            // declaring the run failed on a single dropped request.
            recalcPollRef.current = setTimeout(tick, RECALC_POLL_INTERVAL_MS);
          });
      };

      tick();
    },
    [stopRecalcPoll]
  );

  // Starts (or restarts) the PREPARING poll for `planId` (B2). Same shape as
  // startRecalcPoll: poll getWhatIfRun until the run reaches a terminal
  // proposal (pending_diff_confirmation + proposal) or fails, updating the
  // live stage snapshot in place each tick so PreparingState can render a
  // stage list. A transient poll-request failure keeps polling (a network
  // blip is not a propose failure). Guarded by proposePlanRef so a stale tick
  // or a superseding Run can't act on the wrong plan.
  const startProposePoll = useCallback(
    (planId: string) => {
      stopProposePoll();
      proposePlanRef.current = planId;
      proposeStartRef.current = Date.now();

      const tick = () => {
        if (proposePlanRef.current !== planId) return;

        getWhatIfRun(planId)
          .then((run) => {
            if (proposePlanRef.current !== planId) return;

            // Proposal ready: leave PREPARING for PROPOSAL. Mirrors the
            // reopen path's pending_diff_confirmation + proposal check.
            if (run.status === 'pending_diff_confirmation' && run.proposal) {
              stopProposePoll();
              setActiveRun(planId);
              setCanvas({
                state: 'proposal',
                planId,
                proposal: run.proposal,
                scenarioId: run.scenario_id,
              });
              return;
            }

            // Terminal, non-success (failed/cancelled, or a
            // pending_diff_confirmation with no usable proposal): calm §6
            // PREPARING-fails copy inline on the same slot.
            if (
              run.status === 'failed' ||
              run.status === 'cancelled' ||
              (run.status === 'pending_diff_confirmation' && !run.proposal)
            ) {
              stopProposePoll();
              setCanvas({
                state: 'preparing',
                error:
                  "We couldn't prepare this analysis. Try rewording the change, or pick a different input.",
              });
              return;
            }

            // Still executing: update the stage snapshot in place and
            // reschedule. On timeout, keep the calm spinner (no scary copy) —
            // the poll keeps running, same tolerance as recalc.
            if (Date.now() - proposeStartRef.current > PROPOSE_TIMEOUT_MS) {
              stopProposePoll();
              setCanvas({
                state: 'preparing',
                error:
                  'This is taking longer than usual. You can try again, or come back in a moment.',
              });
              return;
            }
            setCanvas({ state: 'preparing', progress: run.progress });
            proposePollRef.current = setTimeout(tick, PROPOSE_POLL_INTERVAL_MS);
          })
          .catch(() => {
            if (proposePlanRef.current !== planId) return;
            proposePollRef.current = setTimeout(tick, PROPOSE_POLL_INTERVAL_MS);
          });
      };

      tick();
    },
    [stopProposePoll]
  );

  // The canvas scroll region is ONE persistent element across every
  // canvas state, so scroll depth would otherwise carry between states:
  // scrolled to RESULT's bottom links, "View approved changes" opened
  // mid-content with the headline hidden above the fold (screenshot -15,
  // 2026-08-22). Reset on the state NAME only — recalculating's 2 s
  // poll re-sets the same state with fresh progress payloads, and that
  // must never yank the scroll position out from under the reader.
  const canvasScrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    canvasScrollRef.current?.scrollTo({ top: 0 });
  }, [canvas.state]);

  const inputName = useMemo(
    () => options.inputs.find((i) => i.id === form.inputId)?.name ?? '',
    [options.inputs, form.inputId]
  );
  const scenarioName = useMemo(
    () => brand.scenarios.find((s) => s.scenario_id === scenarioId)?.name ?? '',
    [brand.scenarios, scenarioId]
  );
  // The scenario name shown by run-anchored canvases: the run's own
  // scenario when the canvas carries one (reopen paths — see CanvasState),
  // else the current selection (fresh-run flow). Falls back to the
  // current selection if the run's scenario is no longer in the brand's
  // AI-tagged list — the prior behavior, and better than a blank name.
  const canvasScenarioName = useMemo(() => {
    const sid = 'scenarioId' in canvas ? canvas.scenarioId : undefined;
    if (sid == null) return scenarioName;
    return (
      brand.scenarios.find((s) => s.scenario_id === sid)?.name ?? scenarioName
    );
  }, [canvas, brand.scenarios, scenarioName]);

  // Exec AI year window (C1): the instance's chart/download year clip, passed to
  // the time-series surfaces. Both bounds null (unset) = show all years.
  const yearWindow = useMemo<YearWindow>(
    () => ({
      before: brand.exec_ai_years_before,
      after: brand.exec_ai_years_after,
    }),
    [brand.exec_ai_years_before, brand.exec_ai_years_after]
  );

  // HOME's EXAMPLES/RECENT tab (design doc §4). Owned here, not inside
  // HomeState, because HomeState unmounts/remounts on every home ↔
  // non-home canvas transition (conditionally rendered on `canvas.state
  // === 'home'`) — a tab choice stored there would reset every time the
  // executive goes home → confirm → back. WhatIfView does NOT remount
  // on a brand switch either (index.tsx renders it without a
  // brand-keyed `key`), so the tab value is explicitly reset per brand
  // inside the recents-fetch effect. EXAMPLES is always the default
  // (ruled 2026-08-23 — the old one-shot "flip to RECENT once history
  // exists" latch is gone); a manual tab choice still persists across
  // canvas round trips within a brand.
  const [homeTab, setHomeTab] = useState<HomeTab>('examples');

  // Staleness guard for the non-completed recent-click seed fetch below
  // (handleSelectRecent): holds the plan_id of the most recent such
  // click. Same idiom as index.tsx's whatIfOptionsRequestRef — captured
  // right before the request fires, compared in the response handler,
  // so a slower response from an earlier click can't clobber the form
  // with stale data after a second, faster-resolving click on a
  // different row already landed.
  const pendingSeedRequestRef = useRef<string | null>(null);

  // Recents are per-brand history; refetch whenever the instance changes.
  // Also resets the HOME tab to EXAMPLES for the new brand (see
  // homeTab's declaration comment above) — WhatIfView is not remounted
  // on a brand switch, so without this reset a second brand would start
  // on whatever tab the first brand was left on.
  useEffect(() => {
    let cancelled = false;
    setRecentsLoading(true);
    setHomeTab('examples');
    // Also clear the previous brand's recents immediately — they're the
    // old brand's history and must not render for the moment between
    // this reset and the new fetch resolving.
    setRecents([]);
    // Brand switch (index.tsx's Sidebar/BrandPicker; WhatIfView is not
    // remounted per-brand — see this file's header) must also reset the
    // canvas and stop any live RECALCULATING poll: a run in flight for
    // the OLD brand has no business rendering, or ticking a timer,
    // against the NEW brand's pane. activeRun/form are reset alongside
    // it for the same reason — none of that state means anything once
    // the brand underneath it has changed.
    stopRecalcPoll();
    setCanvas({ state: 'home' });
    setActiveRun(null);
    setForm(EMPTY_FORM);
    setActionError(null);
    setDiscarding(false);
    setApproving(false);
    getWhatIfRecent(brand.model_instance_id)
      .then((resp) => {
        if (!cancelled) setRecents(resp.runs ?? []);
      })
      .catch(() => {
        // Recents are a convenience surface — a failed fetch just means
        // the tab stays hidden (empty list), never a blocking error.
        if (!cancelled) setRecents([]);
      })
      .finally(() => {
        if (!cancelled) setRecentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brand.model_instance_id, stopRecalcPoll]);

  // Refresh recents on every return to HOME (2026-08-23): the list was
  // fetched once per brand, so a run created in this session never
  // appeared until a full reload — pop back out after Run and the
  // just-created pending run was missing from the very list that is
  // supposed to reopen it, and row statuses (discarded, completed)
  // went stale. Background refetch: the current list stays rendered
  // until the fresh one lands, no loading flicker; a failure keeps the
  // stale list (recents are a convenience surface, §6 tone). Overlaps
  // with the brand-switch fetch above on mount — a duplicate cap-10 GET,
  // harmless.
  useEffect(() => {
    if (canvas.state !== 'home') return undefined;
    let cancelled = false;
    getWhatIfRecent(brand.model_instance_id)
      .then((resp) => {
        if (!cancelled) setRecents(resp.runs ?? []);
      })
      .catch(() => {
        // Keep whatever list was already showing.
      });
    return () => {
      cancelled = true;
    };
  }, [canvas.state, brand.model_instance_id]);

  const handleChangeHomeTab = useCallback((tab: HomeTab) => {
    setHomeTab(tab);
  }, []);

  // Any manual bar edit while on HOME flips to CONFIRM (§4 HOME row
  // "Exits": "Any manual bar interaction → CONFIRM"). Once already past
  // HOME, edits just update the form in place — the bar is always
  // editable everywhere (D10) without forcing another transition.
  const promoteFromHome = useCallback(() => {
    setCanvas((prev) => (prev.state === 'home' ? { state: 'confirm' } : prev));
  }, []);

  const handleOutputChange = useCallback(
    (outputId: number) => {
      setForm((prev) => ({ ...prev, outputId }));
      promoteFromHome();
    },
    [promoteFromHome]
  );

  const handleInputChange = useCallback(
    (inputId: number) => {
      setForm((prev) => ({ ...prev, inputId }));
      promoteFromHome();
    },
    [promoteFromHome]
  );

  const handleChangeTextChange = useCallback(
    (changeText: string) => {
      setForm((prev) => ({ ...prev, changeText }));
      promoteFromHome();
    },
    [promoteFromHome]
  );

  // Example click (HOME → CONFIRM, §4): seeds the form from the
  // example's frozen params and jumps straight to CONFIRM regardless of
  // current canvas state.
  const handleSelectExample = useCallback((example: WhatIfExample) => {
    setForm({
      outputId: example.params.output_id,
      inputId: example.params.input_id,
      changeText: example.params.change_text,
    });
    setCanvas({ state: 'confirm' });
  }, []);

  // Seeds the form from a fetched run — shared by both branches of a
  // recent click below. The recent row itself only carries
  // title/scenario_name/status (WhatIfRecentRun), not the run's params,
  // so seeding always requires this fetch.
  const seedFormFromRun = useCallback(
    (run: { output_id: number; input_id: number; change_text: string }) => {
      setForm({
        outputId: run.output_id,
        inputId: run.input_id,
        changeText: run.change_text,
      });
    },
    []
  );

  // Recent-reopen path (§4 HOME row: "onOpenRun(plan_id)", D11, task
  // brief "Reopen path"): fetch the run, seed the form from it, and
  // branch on status —
  //   'completed'  -> RESULT rendered straight from the stored result
  //                   (the run's ORIGINAL calculated_at, never a re-run).
  //   'executing'  -> the run is still recalculating (e.g. the exec
  //                   navigated away and came back via a recent click
  //                   mid-run) — enter the same polling flow Approve
  //                   kicks off, so it picks up wherever the backend
  //                   currently is.
  //   'pending_diff_confirmation' (with a stored proposal) -> the LIVE
  //                   PROPOSAL, Approve/Discard armed (2026-08-23
  //                   ruling: an un-actioned proposal reopens as itself,
  //                   not as a status line). Approve/Discard key off
  //                   canvas.planId, so the original run is what gets
  //                   actioned; a fresh Run instead supersedes it (D4).
  //   anything else ('failed', 'cancelled', 'superseded', completed-
  //                   without-result, pending-without-proposal) ->
  //                   CONFIRM, seeded, with the run's status line as
  //                   the notice (2026-08-23 ruling: EVERY recent click
  //                   leaves HOME — the old stay-put silent seed read
  //                   as a dead click, hid the fact that the bar
  //                   changed, and could silently overwrite manual
  //                   edits).
  const handleOpenRun = useCallback(
    (planId: string) => {
      // Same staleness guard as handleSelectRecent's non-completed
      // branch (F4 fix — this branch had none): two rapid opens (e.g.
      // clicking one completed recent, then a second before the first
      // fetch resolves) race two `getWhatIfRun` calls, and without this
      // guard an earlier click's slower response could land AFTER a
      // newer click's and clobber activeRun/form/canvas with stale
      // data. Captured right before the request fires, compared in the
      // response handler — only the response matching the most recent
      // call to this function is applied.
      pendingSeedRequestRef.current = planId;
      // Reopening a run supersedes any in-flight PREPARING poll from a Run the
      // user started but is navigating away from (its guard would no-op stale
      // ticks anyway, but stop the loop so it isn't left ticking).
      stopProposePoll();
      setActiveRun(planId);
      getWhatIfRun(planId)
        .then((run) => {
          if (pendingSeedRequestRef.current !== planId) return;
          seedFormFromRun(run);
          if (run.status === 'completed' && run.result) {
            setCanvas({
              state: 'result',
              planId,
              result: run.result,
              proposal: run.proposal ?? null,
              scenarioId: run.scenario_id,
            });
          } else if (run.status === 'executing') {
            setCanvas({
              state: 'recalculating',
              planId,
              progress: run.progress,
            });
            startRecalcPoll(planId);
          } else if (
            run.status === 'pending_diff_confirmation' &&
            run.proposal
          ) {
            // Un-actioned proposal reopens live. Clear any action state
            // left over from a previous proposal's footer — handleRun
            // does the same for the fresh-run path, and this path
            // bypasses it.
            setActionError(null);
            setDiscarding(false);
            setApproving(false);
            setCanvas({
              state: 'proposal',
              planId,
              proposal: run.proposal,
              scenarioId: run.scenario_id,
            });
          } else {
            // Terminal non-success (and completed-without-result,
            // pending-without-proposal): CONFIRM with the run's status
            // vocabulary as the notice — the seeded form is a
            // re-runnable starting point.
            setCanvas({
              state: 'confirm',
              notice: statusLine(run.status) ?? undefined,
            });
          }
        })
        .catch(() => {
          if (pendingSeedRequestRef.current !== planId) return;
          // A reopen fetch failing is the same as "nothing to show" —
          // stay on HOME rather than jump to a broken result screen.
          setActiveRun(null);
        });
    },
    [seedFormFromRun, startRecalcPoll, stopProposePoll]
  );

  // Recent click (§4 HOME row, amended 2026-08-23): every row routes
  // through handleOpenRun, which navigates by the FETCHED status (the
  // row's own status can be stale) — RESULT, RECALCULATING, or CONFIRM
  // with a status notice. The old non-completed branch seeded the form
  // and stayed on HOME, which read as a dead click. handleOpenRun
  // carries the staleness guard for rapid clicks.
  const handleSelectRecent = useCallback(
    (recent: WhatIfRecentRun) => {
      handleOpenRun(recent.plan_id);
    },
    [handleOpenRun]
  );

  // Remove a FAILED run from the recents list (ruled 2026-08-23):
  // backend soft-hide (dismissed_at in Progress; the ledger keeps the
  // row) + local removal — no refetch needed, since dismiss only
  // happens ON HOME where the list is already fresh, and the local
  // filter matches exactly what the server would now serve. Throws on
  // failure; HomeState (which owns the confirm dialog) surfaces it.
  const handleDismissRecent = useCallback(async (recent: WhatIfRecentRun) => {
    await dismissWhatIfRun(recent.plan_id);
    setRecents((prev) => prev.filter((r) => r.plan_id !== recent.plan_id));
  }, []);

  // "← Examples & recents" (§4, every non-home state): back to HOME,
  // form retained. Does not clear activeRun — reopening the same recent
  // again should be idempotent, and a fresh Run overwrites it anyway.
  // Stops any live RECALCULATING poll first — the honest-timeout promise
  // (task brief: "KEEP polling in the background until unmount") is
  // scoped to staying mounted on the RECALCULATING canvas itself, not to
  // leaving the canvas state entirely; once the executive navigates away
  // from RECALCULATING there is nothing left on screen for a late
  // completion to update; reopening the same run from RECENT still
  // reflects its true current status via a fresh handleOpenRun fetch.
  const handleBackToHome = useCallback(() => {
    stopRecalcPoll();
    setCanvas({ state: 'home' });
  }, [stopRecalcPoll]);

  // §11.2 "View approved changes" (RESULT -> read-only PROPOSAL) and its
  // "← Back to result" return. Both are pure canvas flips between two
  // payloads WhatIfView already has in hand (the 'result' canvas's own
  // proposal/result fields) — no fetch, no backend call, matching the
  // design doc's "cheap to drop... it's a re-render of stored data, no
  // dedicated backend" framing.
  const handleViewApprovedChanges = useCallback(() => {
    setCanvas((prev) => {
      if (prev.state !== 'result' || !prev.proposal) return prev;
      return {
        state: 'proposal_readonly',
        planId: prev.planId,
        proposal: prev.proposal,
        result: prev.result,
        scenarioId: prev.scenarioId,
      };
    });
  }, []);

  const handleBackToResult = useCallback(() => {
    setCanvas((prev) => {
      if (prev.state !== 'proposal_readonly') return prev;
      return {
        state: 'result',
        planId: prev.planId,
        result: prev.result,
        proposal: prev.proposal,
        scenarioId: prev.scenarioId,
      };
    });
  }, []);

  // Run (§4 CONFIRM row: "Run → PREPARING", §6 PREPARING failure copy).
  // Fires proposeWhatIf guarded by runSeq (see its declaration comment):
  // a slower, now-stale response can never land after a newer Run's
  // response (or after the canvas has moved on to PROPOSAL/discard).
  // Clears any stale action state from a previous proposal's footer —
  // otherwise a leftover actionError/discarding flag from a prior run
  // would flash on the new PREPARING → PROPOSAL cycle.
  const handleRun = useCallback(() => {
    runSeq.current += 1;
    const seq = runSeq.current;
    setActionError(null);
    setDiscarding(false);
    setApproving(false);
    // Defensive: the Run button is disabled while `running` (which
    // covers 'recalculating', see the ControlBar props below), so this
    // shouldn't normally fire mid-poll — but stopping unconditionally
    // costs nothing and guarantees a fresh Run can never leave a stale
    // poll ticking against a plan the canvas has moved on from.
    stopRecalcPoll();
    stopProposePoll();
    setCanvas({ state: 'preparing' });

    // B2: propose is kickoff-then-poll. The call now returns
    // {plan_id, status: executing} immediately; the PREPARING poll drives the
    // live stage list and the eventual transition to PROPOSAL. A failed
    // KICKOFF (bad input, entitlement) still rejects here and shows the calm
    // §6 copy; a failed PIPELINE surfaces via the poll seeing status 'failed'.
    proposeWhatIf({
      model_instance_id: brand.model_instance_id,
      scenario_id: scenarioId,
      scenario_names: scenarioName ? [scenarioName] : [],
      output_id: form.outputId as number,
      input_id: form.inputId as number,
      change_text: form.changeText.trim(),
    })
      .then((response) => {
        if (runSeq.current !== seq) return;
        setActiveRun(response.plan_id);
        startProposePoll(response.plan_id);
      })
      .catch(() => {
        if (runSeq.current !== seq) return;
        // §6 PREPARING-fails copy, verbatim.
        setCanvas({
          state: 'preparing',
          error:
            "We couldn't prepare this analysis. Try rewording the change, or pick a different input.",
        });
      });
  }, [
    brand.model_instance_id,
    scenarioId,
    scenarioName,
    form.outputId,
    form.inputId,
    form.changeText,
    stopRecalcPoll,
    stopProposePoll,
    startProposePoll,
  ]);

  // Discard (§4 PROPOSAL row: "Discard → HOME (plan cancelled; form
  // retained)"). A failure leaves the proposal on screen with a calm
  // inline note (§6 tone) rather than guessing at HOME — the executive
  // can just try Discard again, and the form/proposal are both still
  // exactly what they were.
  const handleDiscard = useCallback(() => {
    if (canvas.state !== 'proposal') return;
    const { planId } = canvas;
    setDiscarding(true);
    setActionError(null);
    discardWhatIf(planId)
      .then(() => {
        setDiscarding(false);
        setCanvas({ state: 'home' });
      })
      .catch(() => {
        setDiscarding(false);
        setActionError("We couldn't discard this proposal. Please try again.");
      });
  }, [canvas]);

  // Approve (§4 PROPOSAL row: "Approve → RECALCULATING"). Fires
  // approveWhatIf, transitions to RECALCULATING, and starts the 2 s poll
  // (Task 14). On a failed approve CALL (not a failed recalculation —
  // that's a later poll outcome) the canvas stays on PROPOSAL with a
  // calm note, since nothing was approved.
  const handleApprove = useCallback(() => {
    if (canvas.state !== 'proposal') return;
    const { planId } = canvas;
    setApproving(true);
    setActionError(null);
    approveWhatIf(planId)
      .then(() => {
        setApproving(false);
        setCanvas({ state: 'recalculating', planId });
        startRecalcPoll(planId);
      })
      .catch(() => {
        setApproving(false);
        setActionError(
          "We couldn't start the recalculation. Please try again."
        );
      });
  }, [canvas, startRecalcPoll]);

  const showBackAffordance = canvas.state !== 'home';

  // Cleanup reports false both on leaving RESULT and on unmount (brand change, sidebar nav away from What-If), so the
  // toggle can never stay hidden after the result is gone.
  const resultShown = canvas.state === 'result';
  useEffect(() => {
    if (!resultShown || !onResultShownChange) return undefined;
    onResultShownChange(true);
    return () => onResultShownChange(false);
  }, [resultShown, onResultShownChange]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Nav band (2026-08-22 marks 5 + -11): a slim full-bleed strip
          with a bottom border that hosts the "← Examples & recents"
          back affordance, mirroring the Control Bar's band structure at
          the canvas's other edge. It anchors what used to be a
          free-floating link (glaring in sparse states like PREPARING),
          gives every canvas state a stable top zone, and shares its
          vertical center with the Analyst/Executive toggle overlay
          (ForecastEdgeAITab positions the toggle against this same
          52px band). Rendered on HOME too — empty except the border —
          so the geometry never jumps between states. Sits OUTSIDE the
          scroll region: navigation doesn't scroll away with content. */}
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            ...EXEC_CONTENT_CONTAINER_SX,
            minHeight: 52,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {showBackAffordance && (
            <Button
              size="small"
              startIcon={<ArrowBack />}
              onClick={handleBackToHome}
              // Never yield width to the RESULT disclaimer beside it — without this it wrapped to two lines.
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Examples & recents
            </Button>
          )}
          {/* Client-requested disclaimer (2026-09-28), RESULT only. Lives in the nav band's empty space so it costs
              no vertical space and stays pinned while the result scrolls. Right-aligned to the content edge, 12px; on
              a narrow pane it wraps (two lines still fit the 52px band) — never truncate a disclaimer.
              No space is reserved for ForecastEdgeAITab's overlaid Analyst/Executive toggle: it is hidden while RESULT
              shows (onResultShownChange), and this box renders only on RESULT. */}
          {canvas.state === 'result' && (
            <Box
              sx={{
                ml: 'auto',
                px: 0.75,
                py: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: '1px solid',
                borderColor: 'warning.light',
                borderRadius: 1,
                // Pale amber tint (ruled 2026-09-28): the neutral grey box receded too much.
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
              }}
            >
              <WarningAmberOutlined
                sx={{ fontSize: 16, color: 'warning.main', flexShrink: 0 }}
              />
              <Typography
                sx={{ fontSize: 12, lineHeight: 1.4, color: 'text.primary' }}
              >
                Results are directional, do not reflect full business or
                financial context, and should not be used for decision-making.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Scroll region and content container are DELIBERATELY two boxes
          (2026-08-21 width-bug fix): EXEC_CONTENT_CONTAINER_SX carries
          `mx: 'auto'`, and auto cross-axis margins on a flex-column child
          cancel align-stretch — the old single box shrink-wrapped to its
          content's width (HOME rendered as a ~615px centered column,
          nothing aligned with the Control Bar) and put the scrollbar at
          the shrunken box's edge, mid-pane. The outer box is the
          full-pane-width scroll region (scrollbar at the pane edge, like
          every other executive screen, whose scroll parent is a block);
          the inner block child centers at maxWidth normally. */}
      <Box
        ref={canvasScrollRef}
        // scrollbarGutter 'stable both-edges' (screenshots -18/-19,
        // 2026-08-22): the content container inside is CENTERED
        // (EXEC_CONTENT_CONTAINER_SX mx:'auto'), so when a tall state
        // (RESULT) grows a canvas scrollbar, the inner width shrinks and
        // the centered content shifts ~half a scrollbar left of the
        // Control Bar's identically-centered block below (which never
        // scrolls). Reserving symmetric gutters at all times keeps the
        // canvas centerline equal to the bar's in every state. Safari
        // ignores the property but uses overlay scrollbars that steal no
        // width — the bug can't occur there.
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          scrollbarGutter: 'stable both-edges',
        }}
      >
        <Box
          sx={{
            ...EXEC_CONTENT_CONTAINER_SX,
            py: 3,
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {canvas.state === 'home' && (
            <HomeState
              brand={brand}
              examples={options.examples}
              recents={recents}
              recentsLoading={recentsLoading}
              // Clamp to 'examples' whenever recents is empty, same as the
              // tab control's own hide rule — homeTab can still hold a
              // stale 'recent' value from a brand that had history if the
              // brand switch hasn't repopulated recents yet.
              activeTab={recents.length > 0 ? homeTab : 'examples'}
              onChangeTab={handleChangeHomeTab}
              onSelectExample={handleSelectExample}
              onSelectRecent={handleSelectRecent}
              onDismissRecent={handleDismissRecent}
            />
          )}

          {canvas.state === 'confirm' && (
            <ConfirmState notice={canvas.notice} />
          )}

          {/* PREPARING: no error → PreparingState's spinner + calm line.
            An error → the §6 PREPARING-fails treatment (calm info Alert;
            the "← Examples & recents" affordance above already provides
            the return-home path, so no separate button is duplicated
            here). The form is untouched either way. */}
          {canvas.state === 'preparing' && !canvas.error && (
            <PreparingState progress={canvas.progress} />
          )}
          {canvas.state === 'preparing' && canvas.error && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Alert severity="info" sx={{ maxWidth: 560 }}>
                {canvas.error}
              </Alert>
            </Box>
          )}

          {/* Approve/Discard live in the Control Bar's action cluster
            (2026-08-21 UI review — see ControlBar's header); the canvas
            renders content only, plus the calm actionError alert. */}
          {canvas.state === 'proposal' && (
            <ProposalState
              proposal={canvas.proposal}
              inputName={inputName}
              scenarioName={canvasScenarioName}
              actionError={actionError}
              yearWindow={yearWindow}
            />
          )}

          {/* RECALCULATING (§4, §6): a terminal `error` (failed/cancelled,
            polling already stopped) is the same calm severity="info"
            treatment PREPARING uses for its own failure — the "←
            Examples & recents" affordance above is the path home. No
            error → RecalculatingState's phase-driven body, with the
            timeout copy substituted for the normal phase line while
            polling keeps running underneath (task brief: honest,
            non-destructive — never gate the timeout copy behind a
            terminal state). */}
          {canvas.state === 'recalculating' && canvas.error && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Alert severity="info" sx={{ maxWidth: 560 }}>
                {canvas.error}
              </Alert>
            </Box>
          )}
          {canvas.state === 'recalculating' && !canvas.error && (
            <>
              {canvas.timedOut && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Alert severity="info" sx={{ maxWidth: 560 }}>
                    {RECALC_TIMEOUT_COPY}
                  </Alert>
                </Box>
              )}
              <RecalculatingState progress={canvas.progress} />
            </>
          )}

          {canvas.state === 'result' && (
            <ResultState
              result={canvas.result}
              inputName={inputName}
              scenarioName={canvasScenarioName}
              onViewApprovedChanges={handleViewApprovedChanges}
              yearWindow={yearWindow}
            />
          )}

          {/* §11.2 "View approved changes" — read-only PROPOSAL overlay of
            the completed run's own stored proposal, reachable only from
            RESULT (see this file's header). */}
          {canvas.state === 'proposal_readonly' && (
            <ProposalState
              proposal={canvas.proposal}
              inputName={inputName}
              scenarioName={canvasScenarioName}
              readOnly
              onBackToResult={handleBackToResult}
              yearWindow={yearWindow}
            />
          )}
        </Box>
      </Box>

      <ControlBar
        outputs={options.outputs}
        inputs={options.inputs}
        outputId={form.outputId}
        inputId={form.inputId}
        changeText={form.changeText}
        onOutputChange={handleOutputChange}
        onInputChange={handleInputChange}
        onChangeTextChange={handleChangeTextChange}
        onRun={handleRun}
        running={
          (canvas.state === 'preparing' && !canvas.error) ||
          (canvas.state === 'recalculating' && !canvas.error)
        }
        proposalActions={
          canvas.state === 'proposal'
            ? {
                canApprove: canApproveProposal(canvas.proposal),
                approving,
                discarding,
                onApprove: handleApprove,
                onDiscard: handleDiscard,
              }
            : null
        }
      />
    </Box>
  );
};

export default WhatIfView;
